import { defineMessages, useIntl } from 'react-intl-next';
import React, { useRef } from 'react';

import Button from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';
import { RovoIcon } from '@atlaskit/logo';

import { eventHub } from '@atlassian/editor-plugin-ai/EventHub';

import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';

import type { FloatingToolbarButtonStyleType } from './titleareaContentComponentsTypes';

type SuggestTitleButtonProps = {
	floatingToolbarButtonStyle: FloatingToolbarButtonStyleType;
	isEditorEmpty?: boolean;
	userOffline?: boolean;
};

const i18n = defineMessages({
	suggestTitleLabel: {
		id: 'title-area-content-components.toolbar-above-title.suggest-title-button.label',
		description: 'Text in the button to prompt user to generate a title using Editor AI',
		defaultMessage: 'Suggest title',
	},
	suggestTitleTooltipWhenDocEmpty: {
		id: 'title-area-content-components.toolbar-above-title.suggest-title-button.empty-document.tooltip',
		description:
			'Text in the tooltip which appears on a disabled button to advise user that page content is required',
		defaultMessage: 'Page cannot be empty',
	},
	suggestTitleTooltipWhenLiveDocEmpty: {
		id: 'title-area-content-components.toolbar-above-title.suggest-title-button.empty-live-document.tooltip',
		description:
			'Text in the tooltip which appears on a disabled button to advise user that page content is required',
		defaultMessage: 'Doc cannot be empty',
	},
});

/**
 * Hook that provides a ref (to capture a toolbar element) and a method to retrieve the related
 * editor element's ID (relative to the toolbar element).
 */
export const useEditorId = () => {
	const toolbarButtonRef = useRef<HTMLElement>(null);

	const getTargetEditorId = (): string | undefined => {
		const editors = Array.from(
			toolbarButtonRef.current
				?.closest('.ak-editor-content-area')
				?.querySelectorAll<HTMLElement>('.ProseMirror') ?? [],
		).filter((editor) => !editor.closest('[data-editor-popup]'));

		return editors[0]?.dataset.editorId;
	};

	return { toolbarButtonRef, getTargetEditorId };
};

export const SuggestTitleButton = ({
	floatingToolbarButtonStyle,
	isEditorEmpty,
	userOffline,
}: SuggestTitleButtonProps) => {
	const intl = useIntl();
	const { toolbarButtonRef, getTargetEditorId } = useEditorId();
	const isLivePage = useIsCurrentPageLive();

	const handleClick = () => {
		const targetEditorId = getTargetEditorId();

		if (targetEditorId) {
			eventHub.publishToEditor({
				event: 'start prompt',
				data: {
					targetEditorId,
					adjustSelection: 'fullDocument',
					analyticSourceId: 'confluenceTitleToolbar',
					prompt: 'suggest title in confluence title toolbar',
				},
			});
		}
	};

	const button = (
		<Button
			ref={toolbarButtonRef}
			isDisabled={Boolean(userOffline) || Boolean(isEditorEmpty)}
			appearance="subtle"
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				...floatingToolbarButtonStyle,
			}}
			onClick={handleClick}
			iconBefore={<RovoIcon label="" size="xxsmall" appearance="neutral" />}
		>
			{intl.formatMessage(i18n.suggestTitleLabel)}
		</Button>
	);

	return Boolean(isEditorEmpty) ? (
		<Tooltip
			content={
				isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in')
					? intl.formatMessage(i18n.suggestTitleTooltipWhenLiveDocEmpty)
					: intl.formatMessage(i18n.suggestTitleTooltipWhenDocEmpty)
			}
			position="top-start"
		>
			{button}
		</Tooltip>
	) : (
		button
	);
};
